import { List } from 'immutable'

export const ROLE_ANONYMOUS_USER = 1
export const ROLE_AUTHENTICATED_USER = 2
export const ROLE_SITE_ADMIN = 4
export const ROLE_EDITOR = 3
export const ROLE_EDITOR_PLUS = 21
export const ROLE_DEVELOPER = 5
export const ROLE_CUSTSVC = 7
export const ROLE_INGEST_ADVANCED = 11
export const ROLE_CUSTOMER_SERVICE_MANAGER = 17
export const ROLE_BILLING_SUPPORT = 18
export const ROLE_ARTICLE_EDITOR = 36
export const ROLE_INGEST_BASIC = 41
export const ROLE_QA = 42
export const ROLE_AMBASSADOR_ADMIN = 43
export const ROLE_VIEW_CONTENT_ONLY = 45
export const ROLE_VIEW_MEDIA_ONLY = 46
export const ROLE_MANAGE_PLANS_MARKETING = 50
export const ROLE_MANAGE_PLANS_PRODUCT = 53
export const ROLE_MARKETING_HUB = 54
export const ROLE_WORDPRESS_ARTICLE = 57
export const ROLE_WORDPRESS_LP = 58
export const ROLE_WORDPRESS_PAGE = 59

/** Role ids allowed to access Publishing Hub */
export const PUBLISHING_HUB_ALLOWED_ROLE_IDS = [
  ROLE_SITE_ADMIN,
  ROLE_EDITOR,
  ROLE_DEVELOPER,
  ROLE_INGEST_ADVANCED,
  ROLE_EDITOR_PLUS,
  ROLE_ARTICLE_EDITOR,
  ROLE_INGEST_BASIC,
  ROLE_QA,
  ROLE_CUSTOMER_SERVICE_MANAGER,
  ROLE_BILLING_SUPPORT,
  ROLE_VIEW_CONTENT_ONLY,
  ROLE_VIEW_MEDIA_ONLY,
  ROLE_MANAGE_PLANS_MARKETING,
  ROLE_WORDPRESS_ARTICLE,
  ROLE_WORDPRESS_LP,
  ROLE_WORDPRESS_PAGE,
]

/**
 * Role ids allowed to resolve (Approve / Reject) a moderation queue item.
 * Narrower than MODERATION_PAGE_KEY on purpose: editors read the queue but the
 * `moderationResolve` mutation 403s for them, so the buttons must not render.
 */
export const MODERATION_RESOLVE_ROLE_IDS = [
  ROLE_SITE_ADMIN,
  ROLE_CUSTSVC,
  ROLE_CUSTOMER_SERVICE_MANAGER,
]

/**
 * Whether the signed-in user may Approve / Reject moderation queue items.
 *
 * Resolve is deliberately narrower than the page read: EDITOR and EDITOR_PLUS
 * reach the queue through MODERATION_PAGE_KEY, but the `moderationResolve`
 * mutation 403s for them, so callers hide the actions instead of letting the
 * request fail. Redux holds role ids as an Immutable List while the selector's
 * default (and some callers) hand over a plain array, hence explicit guards
 * rather than assuming iterability.
 *
 * @param {import('immutable').List<number> | number[] | undefined} roleIds
 * @returns {boolean}
 */
export function canResolveModeration (roleIds) {
  if (!List.isList(roleIds) && !Array.isArray(roleIds)) return false
  return MODERATION_RESOLVE_ROLE_IDS.some((id) => roleIds.includes(id))
}

/** Role ids allowed to delete playback companions (pre-roll). EDITOR may create/update only. */
export const PLAYBACK_COMPANION_DELETE_ROLE_IDS = [ROLE_SITE_ADMIN, ROLE_EDITOR_PLUS]

/**
 * @param {import('immutable').List<number> | number[] | undefined} roleIds
 * @returns {boolean}
 */
export function canDeletePlaybackCompanion (roleIds) {
  if (!List.isList(roleIds) && !Array.isArray(roleIds)) return false
  return PLAYBACK_COMPANION_DELETE_ROLE_IDS.some((id) => roleIds.includes(id))
}

/*
  Allowed roles at login time
  used in services/auth/middleware
*/
export const LOGIN_ROLES_ALLOWED = List([
  ROLE_AUTHENTICATED_USER,
  ROLE_SITE_ADMIN,
  ROLE_EDITOR,
  ROLE_CUSTSVC,
  ROLE_AMBASSADOR_ADMIN,
  ROLE_VIEW_CONTENT_ONLY,
  ROLE_VIEW_MEDIA_ONLY,
  ROLE_MARKETING_HUB,
])

/*
  Page level authorization mapping
  - must be implemented by <Page allowedRolesKey={string}/> component to take effect
  - if you are adding a new page and want to lock it down, add an allowedRolesKey to:
    - PAGE_ROLE_MAPPING - with an array containing the roles you will allow on your page
    - <Page /> component - [allowedRolesKey] prop
*/
export const IMAGE_UPLOAD_BULK_PAGE_KEY = 'IMAGE_UPLOAD_BULK_PAGE_KEY'
export const IMAGE_UPLOAD_SIMPLE_PAGE_KEY = 'IMAGE_UPLOAD_SIMPLE_PAGE_KEY'
export const IMAGE_MANAGE_BY_CONTENT_PAGE_KEY = 'IMAGE_MANAGE_BY_CONTENT_PAGE_KEY'
export const TEXT_TRACK_UPLOAD_BULK_PAGE_KEY = 'TEXT_TRACK_UPLOAD_BULK_PAGE_KEY'
export const MERCH_CAMPAIGN_MANAGEMENT_PAGE_KEY = 'MERCH_CAMPAIGN_MANAGEMENT_PAGE_KEY'
export const PLAYBACK_COMPANION_PAGE_KEY = 'PLAYBACK_COMPANION_PAGE_KEY'
export const USER_MANAGEMENT_PAGE_KEY = 'USER_MANAGEMENT_PAGE_KEY'
export const MENU_MANAGEMENT_PAGE_KEY = 'MENU_MANGEMENT_PAGE_KEY'
export const COMMENT_MANAGEMENT_PAGE_KEY = 'COMMENT_MANAGEMENT_PAGE_KEY'
export const REDIRECT_MANAGEMENT_PAGE_KEY = 'REDIRECT_MANAGEMENT_PAGE_KEY'
export const CONTENT_SEARCH_PAGE_KEY = 'CONTENT_SEARCH_PAGE_KEY'
export const NOTIFICATION_CENTER = 'NOTIFICATION_CENTER'
export const YOGA_HOME_PAGE = 'YOGA_HOME_PAGE'
export const USER_TAGS_PAGE_KEY = 'USER_TAGS_PAGE_KEY'
export const EVENT_MANAGEMENT_PAGE_KEY = 'EVENT_MANAGEMENT_PAGE_KEY'
export const ASSETS_MANAGEMENT_PAGE_KEY = 'ASSETS_MANAGEMENT_PAGE_KEY'
export const CONTENT_MANAGEMENT_PAGE_KEY = 'CONTENT_MANAGEMENT_PAGE_KEY'
export const CONTENT_POLICY_MANAGEMENT_PAGE_KEY = 'CONTENT_POLICY_MANAGEMENT_PAGE_KEY'
export const MEDIA_MANAGEMENT_PAGE_KEY = 'MEDIA_MANAGEMENT_PAGE_KEY'
export const FINANCE_PAGE_KEY = 'FINANCE_PAGE_KEY'
export const AMBASSADOR_PAGE_KEY = 'AMBASSADOR_PAGE_KEY'
export const USER_ABUSE_PAGE_KEY = 'USER_ABUSE_PAGE_KEY'
export const USER_MODERATION_PAGE_KEY = 'USER_MODERATION_PAGE_KEY'
export const PLANS_MANAGEMENT_KEY = 'PLANS_MANAGEMENT_KEY'
/** Admin App Marketing Performance Hub (Site Admin or dedicated marketing-hub role). */
export const MARKETING_HUB_PAGE_KEY = 'MARKETING_HUB_PAGE_KEY'
/** QA tools entry (Generate Users, etc.): same gate as user management — no billing; auth-only flows. */
export const QA_GENERATE_USERS_PAGE_KEY = 'QA_GENERATE_USERS_PAGE_KEY'
/** Oracle AI Guide Management (Prompt Library and later AI chat admin). */
export const AI_GUIDE_MANAGEMENT_PAGE_KEY = 'AI_GUIDE_MANAGEMENT_PAGE_KEY'
/** Admin App Publishing Hub (CMS and related staff roles). */
export const PUBLISHING_HUB_PAGE_KEY = 'PUBLISHING_HUB_PAGE_KEY'
/**
 * Comment moderation review queue. Wider than COMMENT_MANAGEMENT_PAGE_KEY: the
 * moderation contract authorises CUSTOMER_SERVICE_MANAGER, which the legacy
 * comments page omits. EDITOR_PLUS stays on this gate — they are readers — but
 * Brooklyn's `moderationQueue` and `moderationOverrides` @auth lists omit that
 * role, so those accounts 403 on both tabs until Brooklyn adds EDITOR_PLUS.
 */
export const MODERATION_PAGE_KEY = 'MODERATION_PAGE_KEY'
/** Rate limiting dashboard and configuration. Site admin only. */
export const RATE_LIMITING_PAGE_KEY = 'RATE_LIMITING_PAGE_KEY'
/** Direct member base reports. Site admin only. The GraphQL field is also SITE_ADMIN. */
export const DIRECT_MEMBER_BASE_PAGE_KEY = 'DIRECT_MEMBER_BASE_PAGE_KEY'

// role arrays are all OR relationship
export const PAGE_ROLE_MAPPING = {
  [USER_ABUSE_PAGE_KEY]: [ROLE_SITE_ADMIN, ROLE_EDITOR, ROLE_EDITOR_PLUS],
  [IMAGE_UPLOAD_BULK_PAGE_KEY]: [ROLE_SITE_ADMIN, ROLE_EDITOR, ROLE_EDITOR_PLUS],
  [IMAGE_UPLOAD_SIMPLE_PAGE_KEY]: [ROLE_SITE_ADMIN, ROLE_EDITOR, ROLE_EDITOR_PLUS],
  [IMAGE_MANAGE_BY_CONTENT_PAGE_KEY]: [ROLE_SITE_ADMIN, ROLE_EDITOR, ROLE_EDITOR_PLUS],
  [TEXT_TRACK_UPLOAD_BULK_PAGE_KEY]: [ROLE_SITE_ADMIN, ROLE_EDITOR, ROLE_EDITOR_PLUS],
  [MERCH_CAMPAIGN_MANAGEMENT_PAGE_KEY]: [ROLE_SITE_ADMIN, ROLE_EDITOR, ROLE_EDITOR_PLUS],
  [PLAYBACK_COMPANION_PAGE_KEY]: [ROLE_SITE_ADMIN, ROLE_EDITOR, ROLE_EDITOR_PLUS],
  [USER_MANAGEMENT_PAGE_KEY]: [ROLE_SITE_ADMIN, ROLE_CUSTSVC, ROLE_AMBASSADOR_ADMIN],
  [COMMENT_MANAGEMENT_PAGE_KEY]: [ROLE_SITE_ADMIN, ROLE_CUSTSVC, ROLE_EDITOR, ROLE_EDITOR_PLUS],
  [REDIRECT_MANAGEMENT_PAGE_KEY]: [
    ROLE_SITE_ADMIN,
    ROLE_EDITOR,
    ROLE_EDITOR_PLUS,
    ROLE_AMBASSADOR_ADMIN,
  ],
  [CONTENT_SEARCH_PAGE_KEY]: [ROLE_SITE_ADMIN, ROLE_EDITOR, ROLE_EDITOR_PLUS],
  [NOTIFICATION_CENTER]: [ROLE_SITE_ADMIN, ROLE_EDITOR, ROLE_EDITOR_PLUS],
  [MENU_MANAGEMENT_PAGE_KEY]: [ROLE_SITE_ADMIN],
  [YOGA_HOME_PAGE]: [ROLE_SITE_ADMIN, ROLE_EDITOR, ROLE_EDITOR_PLUS],
  [EVENT_MANAGEMENT_PAGE_KEY]: [ROLE_SITE_ADMIN, ROLE_EDITOR, ROLE_EDITOR_PLUS],
  [ASSETS_MANAGEMENT_PAGE_KEY]: [ROLE_SITE_ADMIN, ROLE_EDITOR, ROLE_EDITOR_PLUS],
  [USER_MODERATION_PAGE_KEY]: [ROLE_SITE_ADMIN, ROLE_CUSTSVC],
  [USER_TAGS_PAGE_KEY]: [ROLE_SITE_ADMIN, ROLE_CUSTSVC],
  [CONTENT_MANAGEMENT_PAGE_KEY]: [
    ROLE_SITE_ADMIN,
    ROLE_EDITOR,
    ROLE_EDITOR_PLUS,
    ROLE_VIEW_CONTENT_ONLY,
    ROLE_VIEW_MEDIA_ONLY,
  ],
  [CONTENT_POLICY_MANAGEMENT_PAGE_KEY]: [ROLE_SITE_ADMIN, ROLE_EDITOR_PLUS],
  [MEDIA_MANAGEMENT_PAGE_KEY]: [
    ROLE_SITE_ADMIN,
    ROLE_EDITOR,
    ROLE_EDITOR_PLUS,
    ROLE_VIEW_MEDIA_ONLY,
  ],
  [FINANCE_PAGE_KEY]: [ROLE_SITE_ADMIN, ROLE_CUSTSVC],
  [AMBASSADOR_PAGE_KEY]: [ROLE_AMBASSADOR_ADMIN],
  [PLANS_MANAGEMENT_KEY]: [ROLE_SITE_ADMIN, ROLE_MANAGE_PLANS_MARKETING, ROLE_MANAGE_PLANS_PRODUCT],
  [MARKETING_HUB_PAGE_KEY]: [ROLE_SITE_ADMIN, ROLE_MARKETING_HUB],
  [QA_GENERATE_USERS_PAGE_KEY]: [ROLE_SITE_ADMIN, ROLE_CUSTSVC, ROLE_AMBASSADOR_ADMIN],
  [AI_GUIDE_MANAGEMENT_PAGE_KEY]: [ROLE_SITE_ADMIN, ROLE_EDITOR, ROLE_EDITOR_PLUS],
  [PUBLISHING_HUB_PAGE_KEY]: PUBLISHING_HUB_ALLOWED_ROLE_IDS,
  [MODERATION_PAGE_KEY]: [
    ROLE_SITE_ADMIN,
    ROLE_CUSTSVC,
    ROLE_CUSTOMER_SERVICE_MANAGER,
    ROLE_EDITOR,
    ROLE_EDITOR_PLUS,
  ],
  [RATE_LIMITING_PAGE_KEY]: [ROLE_SITE_ADMIN],
  [DIRECT_MEMBER_BASE_PAGE_KEY]: [ROLE_SITE_ADMIN],
}
